import { Cookie } from '@phosphor-icons/react'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useRef, useState } from 'react'

import Button from '@/components/shared/ui/Button'
import Checkbox from '@/components/shared/ui/Checkbox/Checkbox'
import Dialog from '@/components/shared/ui/Dialog'
import {
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/shared/ui/Dialog/Dialog'
import ChatSnippets from '@/components/users/shared/integration_snippets/ChatSnippets'
import Snippets from '@/components/users/shared/integration_snippets/Snippets'
import { cookiePolicyURL } from '@/config/staticURLs'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import {
  getAllCookieFlag,
  getCookieFlag,
  updateAllCookieFlag,
  updateCookieFlag,
} from '@/lib/Cookie'
import { EventEmitter } from '@/lib/eventEmitter'
import {
  changelogWidgetPath,
  embedPath,
  postWidgetPath,
  signinPath,
  signupPath,
  submissionOnlyPath,
} from '@/lib/helpers/pathHelpers'

// DO_NOT_SHOW
const DISALLOWED_PATHS = [
  embedPath,
  postWidgetPath,
  submissionOnlyPath,
  changelogWidgetPath,
  submissionOnlyPath,
  signupPath,
  signinPath,
]

export default function CookieBanner() {
  const t = useTranslations('alerts.cookieBanner')

  const router = useRouter()
  const [show, setShow] = useState(!getCookieFlag())
  const { isWidget, userProfile } = useContext(HNContext)
  const currentFlags = getAllCookieFlag()
  const [showDialog, setShowDialog] = useState(false)
  const [allowedCookie, setAllowedCookies] = useState(
    {
      analytics: currentFlags?.analytics || 'true',
      functionality: currentFlags?.functionality || 'true',
    } || { analytics: 'true', functionality: 'true' }
  )

  const config = useRef({
    analytics: allowedCookie.analytics,
    functionality: allowedCookie.functionality,
  })

  const handleClose = () => {
    updateCookieFlag(true.toString())
    setShow(false)
    setShowDialog(false)
  }

  const handleAllowAll = () => {
    updateAllCookieFlag({
      analytics: 'true',
      functionality: 'true',
    })
    setAllowedCookies({
      analytics: 'true',
      functionality: 'true',
    })
    handleClose()
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setAllowedCookies({
      analytics: config.current.analytics,
      functionality: config.current.functionality,
    })
    updateAllCookieFlag({
      analytics: config.current.analytics,
      functionality: config.current.functionality,
    })
    handleClose()
  }

  const handleCookieModalEvent = () => {
    setShowDialog(true)
  }

  useEffect(() => {
    EventEmitter.subscribe('OPEN_COOKIES_MODAL', handleCookieModalEvent)
    return () =>
      EventEmitter.subscribe('OPEN_COOKIES_MODAL', handleCookieModalEvent)
  }, [])

  if (DISALLOWED_PATHS.some((path) => router.pathname.includes(path)))
    return null

  return (
    <>
      {!!(show && userProfile?.id) && (
        <div className='pointer-events-none fixed inset-x-0 bottom-4 flex items-center justify-center'>
          <div
            className='pointer-events-auto max-w-xl rounded-2xl border border-gray7 bg-gray1 p-4 text-left font-medium shadow-2xl'
            style={{
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
            }}
          >
            <div className='flex flex-col gap-2'>
              <div className=''>
                <Cookie
                  weight='fill'
                  className='h-8 w-8 shrink-0 fill-amber11'
                />
                <div>
                  <p className='text-lg font-semibold text-gray11'>
                    {t('title')}
                  </p>
                  <p
                    className='mb-1 text-gray11 transition [&>a]:text-primary'
                    dangerouslySetInnerHTML={{
                      __html: t('description', {
                        data: {
                          link: cookiePolicyURL,
                        },
                      }),
                    }}
                  />
                </div>
              </div>
              <div className='flex gap-1'>
                <Button size='xs' onClick={handleAllowAll}>
                  {t('acceptAll')}
                </Button>
                <Button size='xs' onClick={handleClose} variant='outline'>
                  {t('acceptNecessary')}
                </Button>
                <Button
                  size='xs'
                  onClick={() => {
                    setShowDialog(true)
                  }}
                  className={'self-end'}
                  variant='naked'
                >
                  {t('customize')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {allowedCookie.analytics === 'true' && !isWidget && <Snippets />}
      {allowedCookie.functionality === 'true' && !isWidget && (
        <>
          <ChatSnippets />
        </>
      )}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogClose />
          <div className='flex flex-col gap-3'>
            <DialogTitle>{t('info.title')}</DialogTitle>
            <p className='text-gray11'>{t('info.description')}</p>
            <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
              <Checkbox
                isSwitch
                label={t('info.essential.title')}
                disabled
                checked
                description={t('info.essential.description')}
              />
              <Checkbox
                isSwitch
                label={t('info.analytics.title')}
                onCheckedChange={(checked) => {
                  config.current.analytics = checked.toString()
                }}
                defaultChecked={allowedCookie.analytics === 'true'}
                description={t('info.analytics.description')}
              />
              <Checkbox
                isSwitch
                label={t('info.functionality.title')}
                onCheckedChange={(checked) => {
                  config.current.functionality = checked.toString()
                }}
                defaultChecked={allowedCookie.functionality === 'true'}
                description={t('info.functionality.description')}
              />
              <Button type='submit'>
                {t('buttons.save', {
                  ignorePrefix: true,
                })}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
